import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  ZoomOut, 
  Download,
  RotateCw,
  X,
  FileText,
  AlertCircle,
  Loader,
  Search,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { PDF_VIEWER_CONFIG } from '../../constants/pdfViewer';
import { useAuth0 } from '@auth0/auth0-react';

// Set up PDF.js worker - use local worker file from public directory
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PDFViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  filename?: string;
  searchText?: string;
  highlightText?: string;
}

interface SearchMatch {
  pageNumber: number;
  matchIndex: number;
  text: string;
}

export const PDFViewerModal: React.FC<PDFViewerModalProps> = ({ 
  isOpen,
  onClose,
  url, 
  filename = 'document.pdf',
  searchText = '',
  highlightText
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(PDF_VIEWER_CONFIG.DEFAULT_SCALE);
  const [rotation, setRotation] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>(searchText);
  const [searchMatches, setSearchMatches] = useState<SearchMatch[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(-1);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const pdfDocumentRef = useRef<any>(null);
  const { getAccessTokenSilently } = useAuth0();
  const [fileSource, setFileSource] = useState<any>(null);

  // Build an authenticated file source for react-pdf
  useEffect(() => {
    if (!isOpen) return;
    
    let isMounted = true;
    const buildFileSource = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "openid profile email"
          }
        });
        if (!isMounted) return;
        setFileSource({
          url,
          httpHeaders: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/pdf'
          },
        });
      } catch (e) {
        // Fallback to unauthenticated URL if token retrieval fails
        if (!isMounted) return;
        setFileSource({ url });
      }
    };
    buildFileSource();
    return () => {
      isMounted = false;
    };
  }, [url, getAccessTokenSilently, isOpen]);

  // Initialize search when modal opens with searchText
  useEffect(() => {
    if (isOpen && searchText && searchText !== searchQuery) {
      setSearchQuery(searchText);
    }
  }, [isOpen, searchText]);

  // Perform search when query changes and PDF is loaded
  useEffect(() => {
    if (searchQuery && pdfDocumentRef.current && numPages > 0) {
      performSearch(searchQuery);
    } else {
      setSearchMatches([]);
      setCurrentMatchIndex(-1);
      clearHighlights();
    }
  }, [searchQuery, numPages]);

  const onDocumentLoadSuccess = useCallback((pdfDoc: any) => {
    setNumPages(pdfDoc.numPages);
    setIsLoading(false);
    setError(null);
    // Store document reference for text search
    pdfDocumentRef.current = pdfDoc;
  }, []);

  const onDocumentLoadError = useCallback((error: any) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF. Please check if the file exists and is accessible.');
    setIsLoading(false);
  }, []);

  const clearHighlights = useCallback(() => {
    // Remove existing highlights
    const existingHighlights = document.querySelectorAll('.pdf-text-highlight');
    existingHighlights.forEach(highlight => {
      const parent = highlight.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(highlight.textContent || ''), highlight);
        parent.normalize();
      }
    });
  }, []);

  const addTextHighlights = useCallback((query: string) => {
    if (!query) {
      clearHighlights();
      return;
    }

    // Wait for PDF page to fully render
    setTimeout(() => {
      try {
        const textLayers = document.querySelectorAll('.react-pdf__Page__textContent');
        
        // Get search terms (split by spaces, filter short words)
        const searchTerms = query.toLowerCase().split(/\s+/).filter(term => term.length > 2);
        
        textLayers.forEach(textLayer => {
          const textSpans = textLayer.querySelectorAll('span');
          
          textSpans.forEach(span => {
            if (span.textContent && !span.querySelector('.pdf-text-highlight')) {
              const originalText = span.textContent;
              let highlightedText = originalText;
              let hasHighlight = false;
              
              // Try to highlight each search term
              searchTerms.forEach(term => {
                const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                if (regex.test(originalText)) {
                  highlightedText = highlightedText.replace(
                    regex, 
                    '<mark class="pdf-text-highlight" style="background-color: #fde047; color: #1f2937; padding: 1px 2px; border-radius: 2px;">$1</mark>'
                  );
                  hasHighlight = true;
                }
              });
              
              // Also try to highlight the full query if it's short enough
              if (query.length <= 50) {
                const fullQueryRegex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                if (fullQueryRegex.test(originalText)) {
                  highlightedText = originalText.replace(
                    fullQueryRegex,
                    '<mark class="pdf-text-highlight" style="background-color: #fde047; color: #1f2937; padding: 1px 2px; border-radius: 2px;">$1</mark>'
                  );
                  hasHighlight = true;
                }
              }
              
              if (hasHighlight) {
                span.innerHTML = highlightedText;
              }
            }
          });
        });
        
        // Scroll to first highlight if exists
        const firstHighlight = document.querySelector('.pdf-text-highlight');
        if (firstHighlight) {
          firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch (error) {
        console.error('Highlighting error:', error);
      }
    }, 200);
  }, [clearHighlights]);

  const performSearch = async (query: string) => {
    if (!query || !pdfDocumentRef.current) {
      console.log('Search skipped: no query or PDF document');
      return;
    }

    console.log('Starting search for:', query);
    setIsSearching(true);
    const matches: SearchMatch[] = [];
    
    try {
      // Search through all pages
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDocumentRef.current.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        // Combine all text items into a single string with spaces
        const pageText = textContent.items
          .map((item: any) => item.str || '')
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();

        if (pageNum === 1) {
          console.log('Sample text from page 1:', pageText.substring(0, 200));
        }

        // Search for the query (case insensitive, allowing for some flexibility)
        const searchTerms = query.toLowerCase().split(/\s+/).filter(term => term.length > 2);
        let hasMatch = false;
        let matchedTerms: string[] = [];
        
        // Check if any significant search terms are found
        for (const term of searchTerms) {
          if (pageText.toLowerCase().includes(term)) {
            hasMatch = true;
            matchedTerms.push(term);
          }
        }
        
        // Also check for partial matches or the full query
        if (!hasMatch && pageText.toLowerCase().includes(query.toLowerCase())) {
          hasMatch = true;
          matchedTerms.push(query);
        }
        
        if (hasMatch) {
          console.log(`Found match on page ${pageNum} with terms:`, matchedTerms);
          matches.push({
            pageNumber: pageNum,
            matchIndex: matches.length,
            text: query
          });
        }
      }
    } catch (error) {
      console.error('Search error:', error);
    }

    console.log('Search completed. Found', matches.length, 'matching pages');
    setSearchMatches(matches);
    setCurrentMatchIndex(matches.length > 0 ? 0 : -1);
    
    // Navigate to first match
    if (matches.length > 0) {
      console.log('Navigating to page', matches[0].pageNumber);
      setPageNumber(matches[0].pageNumber);
      // Add a delay to ensure page renders before highlighting
      setTimeout(() => {
        console.log('Attempting to highlight text');
        addTextHighlights(query);
      }, 600);
    } else {
      console.log('No matches found for query:', query);
    }
    
    setIsSearching(false);
  };

  const goToPrevPage = useCallback(() => {
    setPageNumber(prev => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber(prev => Math.min(numPages, prev + 1));
  }, [numPages]);

  const goToPrevMatch = useCallback(() => {
    if (searchMatches.length === 0) return;
    const newIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : searchMatches.length - 1;
    setCurrentMatchIndex(newIndex);
    setPageNumber(searchMatches[newIndex].pageNumber);
    // Re-highlight after page change
    setTimeout(() => addTextHighlights(searchQuery), 300);
  }, [searchMatches, currentMatchIndex, searchQuery, addTextHighlights]);

  const goToNextMatch = useCallback(() => {
    if (searchMatches.length === 0) return;
    const newIndex = currentMatchIndex < searchMatches.length - 1 ? currentMatchIndex + 1 : 0;
    setCurrentMatchIndex(newIndex);
    setPageNumber(searchMatches[newIndex].pageNumber);
    // Re-highlight after page change
    setTimeout(() => addTextHighlights(searchQuery), 300);
  }, [searchMatches, currentMatchIndex, searchQuery, addTextHighlights]);

  // Re-highlight when page changes
  useEffect(() => {
    if (searchQuery && numPages > 0) {
      // Clear existing highlights first, then add new ones
      clearHighlights();
      setTimeout(() => addTextHighlights(searchQuery), 400);
    }
  }, [pageNumber, searchQuery, addTextHighlights, clearHighlights, numPages]);

  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(PDF_VIEWER_CONFIG.ZOOM_MAX, prev + PDF_VIEWER_CONFIG.ZOOM_STEP));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => Math.max(PDF_VIEWER_CONFIG.ZOOM_MIN, prev - PDF_VIEWER_CONFIG.ZOOM_STEP));
  }, []);

  const resetZoom = useCallback(() => {
    setScale(PDF_VIEWER_CONFIG.DEFAULT_SCALE);
  }, []);

  const rotate = useCallback(() => {
    setRotation(prev => (prev + PDF_VIEWER_CONFIG.ROTATION_STEP) % 360);
  }, []);

  const downloadPDF = useCallback(async () => {
    setIsDownloading(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email"
        }
      });

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf',
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [url, filename, getAccessTokenSilently]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery) {
      performSearch(searchQuery);
    }
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      goToNextMatch();
    } else if (e.key === 'Enter' && e.shiftKey) {
      goToPrevMatch();
    }
  }, [isOpen, onClose, goToNextMatch, goToPrevMatch]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="w-full h-full max-w-7xl mx-4 bg-white dark:bg-gray-900 rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {filename}
              </h2>
            </div>
            
            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search in document..."
                  className="pl-8 pr-3 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              
              {searchMatches.length > 0 && (
                <div className="flex items-center space-x-1">
                  <button
                    type="button"
                    onClick={goToPrevMatch}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    title="Previous match (Shift+Enter)"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400 px-2">
                    {currentMatchIndex + 1} of {searchMatches.length}
                  </span>
                  <button
                    type="button"
                    onClick={goToNextMatch}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                    title="Next match (Ctrl+Enter)"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {isSearching && (
                <Loader className="w-4 h-4 animate-spin text-blue-500" />
              )}
            </form>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center space-x-2">
            {/* Page Navigation */}
            <div className="flex items-center space-x-1">
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center space-x-1 px-2 text-sm">
                <input
                  type="number"
                  min={1}
                  max={numPages}
                  value={pageNumber}
                  onChange={(e) => {
                    const page = parseInt(e.target.value);
                    if (page >= 1 && page <= numPages) {
                      setPageNumber(page);
                    }
                  }}
                  className="w-16 text-center border rounded px-1 py-0.5 text-xs dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-gray-500 dark:text-gray-400">of {numPages}</span>
              </div>
              <button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center space-x-1 border-l border-gray-300 dark:border-gray-600 pl-2">
              <button
                onClick={zoomOut}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Zoom out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                onClick={resetZoom}
                className="px-2 py-1 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-700 min-w-[3rem]"
                title="Reset zoom"
              >
                {Math.round(scale * 100)}%
              </button>
              <button
                onClick={zoomIn}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Zoom in"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Rotate Control */}
            <div className="flex items-center border-l border-gray-300 dark:border-gray-600 pl-2">
              <button
                onClick={rotate}
                className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                title="Rotate"
              >
                <RotateCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={downloadPDF}
              disabled={isDownloading}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={isDownloading ? "Downloading..." : "Download PDF"}
            >
              {isDownloading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900">
          {error ? (
            <div className="flex flex-col items-center justify-center h-full p-8">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">
                PDF Loading Error
              </h3>
              <p className="text-sm text-red-600 dark:text-red-300 text-center">
                {error}
              </p>
            </div>
          ) : !fileSource ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center">
                <Loader className="w-8 h-8 animate-spin text-blue-500 mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading PDF...</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center p-4">
              <Document
                file={fileSource}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading=""
                error=""
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  rotate={rotation}
                  renderTextLayer={true}
                  renderAnnotationLayer={false}
                  className="shadow-lg"
                />
              </Document>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoading && !error && (
          <div className="flex items-center justify-between p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <span>Page {pageNumber} of {numPages}</span>
              <span>Scale: {Math.round(scale * 100)}%</span>
              {searchMatches.length > 0 && (
                <span>{searchMatches.length} matches found</span>
              )}
            </div>
            <div className="text-xs text-gray-400">
              Use Ctrl+Enter / Shift+Enter to navigate matches • Esc to close
            </div>
          </div>
        )}
      </div>
    </div>
  );
};